import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Layout from "../components/layout/Layout";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import {
  getPortalWideLpe1Section12Defaults,
  getPropertyDetails,
  updatePropertyMetadata,
} from "../api/propertyApi";
import { getFME1CheckboxSchema, getFME1FormFieldSchema } from "../api/fme1Api";
import { getApiErrorMessage } from "../utils/concurrency";

const FME1_SECTION_TITLES = {
  1: "Section 1 - Property & Contact Details",
  2: "Section 2 - Transfer & Registration",
  3: "Section 3 - Service Charge",
  4: "Section 4 - Insurance",
  5: "Section 5 - Disputes",
  6: "Section 6 - Required Documents",
};

const FME1_COMPOSITE_CHECKBOX_GROUPS = [
  {
    keys: ["2.3 membership_required_yes", "2.3 membership_required_no"],
    question: "2.3 Is the incoming Owner required to take a share in, or become a member of, the Management Company?",
    options: [
      { label: "Yes", key: "2.3 membership_required_yes" },
      { label: "No", key: "2.3 membership_required_no" },
    ],
  },
  {
    keys: ["3.7.2 reserve_fund_sufficient_yes", "3.7.2 reserve_fund_sufficient_no"],
    question: "3.7.2 Is the amount expected to be sufficient to cover known expenditure?",
    options: [
      { label: "Yes", key: "3.7.2 reserve_fund_sufficient_yes" },
      { label: "No", key: "3.7.2 reserve_fund_sufficient_no" },
    ],
  },
];

const FME1_COMPOSITE_CHECKBOX_BY_KEY = FME1_COMPOSITE_CHECKBOX_GROUPS.reduce((map, group) => {
  group.keys.forEach((key) => {
    map[key] = group;
  });
  return map;
}, {});

const FME1_PORTAL_MANAGING_AGENT_KEYS = new Set([
  "1.3 Name",
  "1.3 Address",
  "1.3 Telephone",
  "1.3 Email",
  "1.3 SC",
  "1.3 Account Name",
  "1.3 Account number",
  "1.3 Reference",
  "1.3 Fee Type",
]);

const FME1_NOTICE_PARTIES = [
  { label: "Rentcharge Owner", selectedKey: "1.5 rentcharge_owner_accepts_notice", emailKey: "1.5 rentcharge_owner_accepts_notice_email", amountKeys: ["1.6 RO £"] },
  { label: "Management Company", selectedKey: "1.5 management_company_accepts_notice", emailKey: "1.5 management_company_accepts_notice_email", amountKeys: ["1.6 MC £"] },
  { label: "Managing Agent", selectedKey: "1.5 managing_agent_accepts_notice", emailKey: "1.5 managing_agent_accepts_notice_email", amountKeys: ["1.6 MA £"] },
  { label: "Legal Representative", selectedKey: "1.5 legal_representative_accepts_notice", emailKey: "1.5 legal_representative_accepts_notice_email", amountKeys: ["1.6 LR £"] },
  { label: "Other", selectedKey: "1.5 other_accepts_notice", emailKey: "1.5 other_accepts_notice_email", amountKeys: ["1.6 Other £"] },
];

const FME1_NOTICE_FIELD_KEYS = new Set([
  ...FME1_NOTICE_PARTIES.flatMap((party) => party.amountKeys),
  "1.6 Name",
  "1.6 Address",
  "1.6 Telephone",
  "1.6 Email",
  "1.6 Capacity eg Landlords lawyer",
  "1.5.1",
]);

const FME1_NOTICE_CHECKBOX_KEYS = new Set([
  ...FME1_NOTICE_PARTIES.flatMap((party) => [party.selectedKey, party.emailKey]),
  "1.5.1 requires_additional_details",
]);

const isNoticeField = (field) =>
  FME1_NOTICE_FIELD_KEYS.has(field.field_key) || FME1_NOTICE_CHECKBOX_KEYS.has(field.key);

const isCheckedValue = (value) => value === "Checked";

const isInRange = (value, range) => {
  if (!range?.length) return false;
  return value >= range[0] && value <= range[1];
};

const contactFieldLabels = (prefix, roleName, { redress = false } = {}) => ({
  [`${prefix} Name`]: `${prefix} ${roleName} Name`,
  [`${prefix} Address`]: `${prefix} ${roleName} Address`,
  [`${prefix} Telephone`]: `${prefix} ${roleName} Telephone`,
  [`${prefix} Email`]: `${prefix} ${roleName} Email`,
  ...(redress ? { [`${prefix} Redress`]: `${prefix} ${roleName} Redress Scheme` } : {}),
  [`${prefix} SC`]: `${prefix} ${roleName} Sort Code`,
  [`${prefix} Account Name`]: `${prefix} ${roleName} Account Name`,
  [`${prefix} Account number`]: `${prefix} ${roleName} Account number`,
  [`${prefix} Reference`]: `${prefix} ${roleName} Reference`,
  [`${prefix} Fee Type`]: `${prefix} ${roleName} Fee Type`,
});

const FME1_SECTION_GROUPS = {
  1: [
    { fieldKeys: ["Property", "Post CodeSeller", "Seller", "Post CodeUPRN if known"] },
    {
      title: "1.1 Rentcharge Owner",
      fieldOrders: [[4, 13]],
      fieldKeys: ["1.1 Name", "1.1 Address", "1.1 Telephone", "1.1 Email", "1.1 Redress", "1.1 SC", "1.1 Account Name", "1.1 Account number", "1.1 Reference", "1.1 Fee Type"],
      fieldLabelOverrides: contactFieldLabels("1.1", "Rentcharge Owner", { redress: true }),
      labelPrefix: "1.1",
      roleName: "Rentcharge Owner",
    },
    {
      title: "1.2 Management Company",
      fieldOrders: [[14, 23]],
      fieldKeys: ["1.2 Name", "1.2 Address", "1.2 Telephone", "1.2 Email", "1.2 Redress", "1.2 SC", "1.2 Account Name", "1.2 Account number", "1.2 Reference", "1.2 Fee Type"],
      fieldLabelOverrides: contactFieldLabels("1.2", "Management Company", { redress: true }),
      labelPrefix: "1.2",
      roleName: "Management Company",
    },
    {
      title: "1.3 Managing Agent",
      fieldOrders: [[24, 33]],
      fieldKeys: ["1.3 Name", "1.3 Address", "1.3 Telephone", "1.3 Email", "1.3 Redress", "1.3 SC", "1.3 Account Name", "1.3 Account number", "1.3 Reference", "1.3 Fee Type"],
      fieldLabelOverrides: contactFieldLabels("1.3", "Managing Agent", { redress: true }),
      labelPrefix: "1.3",
      roleName: "Managing Agent",
    },
    {
      title: "1.4 Legal Representative of one of the above",
      fieldOrders: [[34, 42]],
      fieldKeys: ["1.4 Name", "1.4 Address", "1.4 Telephone", "1.4 Email", "1.4 SC", "1.4 Account Name", "1.4 Account number", "1.4 Reference", "1.4 Fee Type"],
      fieldLabelOverrides: contactFieldLabels("1.4", "Legal Representative"),
      labelPrefix: "1.4",
      roleName: "Legal Representative",
      checkboxOrders: [0, 0],
    },
    { noticeLayout: true },
    { title: "Responsibilities", checkboxOrders: [12, 14] },
  ],
  2: [
    { fieldKeys: ["2.1.1 £", "2.1.2"], checkboxOrders: [15, 15] },
    { fieldKeys: ["2.2.1"], checkboxOrders: [16, 16] },
    { fieldKeys: ["2.3.1"], checkboxOrders: [17, 18] },
    { fieldKeys: ["2.4"], question: "2.4 What is the procedure and cost for obtaining a certificate in accordance with a restriction in the Proprietorship Register at the Land Registry, if applicable?", fieldLabelOverrides: { "2.4": "Details" } },
  ],
  3: [
    { fieldKeys: ["3.1 £", "3.1"], annualServiceChargeLayout: true },
    { fieldKeys: ["3.2.1"], checkboxOrders: [19, 19] },
    {
      fieldKeys: ["3.3 - From DD", "3.3 - From MM", "3.3 - From YYYY", "3.3 - To DD", "3.3 - To MM", "3.3 - To YYYY"],
      periodFields: {
        question: "3.3 What period is covered by the last demand?",
        from: ["3.3 - From DD", "3.3 - From MM", "3.3 - From YYYY"],
        to: ["3.3 - To DD", "3.3 - To MM", "3.3 - To YYYY"],
      },
    },
    {
      fieldKeys: ["3.1 RD", "3.1 RL", "3.1 COM"],
      question: "3.4 How many properties contribute toward the maintenance of the Managed Area? (Stipulate the number of each applicable type of property)",
      fieldRows: [
        { key: "3.1 RD", label: "Residential Freehold" },
        { key: "3.1 RL", label: "Residential Leasehold" },
        { key: "3.1 COM", label: "Commercial" },
      ],
    },
    { fieldKeys: ["3.4.1.1"], checkboxOrders: [20, 20] },
    { fieldKeys: ["3.5.1"], checkboxOrders: [21, 21] },
    { fieldKeys: ["3.6.1"], checkboxOrders: [22, 22] },
    { fieldKeys: ["3.7.1 £", "3.7.1 Managed Areas £", "3.7.3"], checkboxOrders: [23, 25], reserveFundLayout: true },
    {
      fieldKeys: ["3.8 ID DD", "3.8 ID MM", "3.8 ID YYYY", "3.8 ED DD", "3.8 ED MM", "3.8 ED YYYY"],
      checkboxOrders: [26, 27],
      dateRows: {
        question:
          "3.8 If parts of the Managed Areas require regular decoration confirm the date when the Managed Areas were last decorated, internally and externally.",
        rows: [
          { label: "Internally:", fields: ["3.8 ID DD", "3.8 ID MM", "3.8 ID YYYY"], naKey: "3.8 internal_na" },
          { label: "Externally:", fields: ["3.8 ED DD", "3.8 ED MM", "3.8 ED YYYY"], naKey: "3.8 external_na" },
        ],
      },
    },
    { fieldKeys: ["3.9.1"], checkboxOrders: [28, 28] },
    { fieldKeys: ["3.10.1"], checkboxOrders: [29, 29] },
    { fieldKeys: ["3.11.1"], checkboxOrders: [30, 30] },
    { fieldKeys: ["3.12.1"], checkboxOrders: [31, 31] },
    { fieldKeys: ["3.13.1"], checkboxOrders: [32, 32] },
  ],
  4: [
    { fieldKeys: ["4.1.1.1"], checkboxOrders: [33, 34] },
    {
      fieldKeys: ["4.2 From DD", "4.2 From MM", "4.2 From YYYY", "4.2 To DD", "4.2 To MM", "4.2 To YYYY"],
      periodFields: {
        question: "4.2 What period is covered by the last demand?",
        from: ["4.2 From DD", "4.2 From MM", "4.2 From YYYY"],
        to: ["4.2 To DD", "4.2 To MM", "4.2 To YYYY"],
      },
    },
    { fieldKeys: ["4.3.1"], checkboxOrders: [35, 35] },
    { fieldKeys: ["4.4.1"], checkboxOrders: [36, 36] },
    { checkboxOrders: [37, 37] },
    { fieldKeys: ["4.6"] },
    { fieldKeys: ["4.7.1"], checkboxOrders: [38, 38] },
    { fieldKeys: ["4.8.1"], checkboxOrders: [39, 39] },
  ],
  5: [
    { fieldKeys: ["5.1.1"], checkboxOrders: [40, 40] },
    { fieldKeys: ["5.2.1"], checkboxOrders: [41, 41] },
  ],
  6: [{ checkboxOrders: [42, 56] }],
};

export default function PropertyFME1MetadataPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const [property, setProperty] = useState(null);
  const [textSchema, setTextSchema] = useState([]);
  const [checkboxSchema, setCheckboxSchema] = useState([]);
  const draftRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [metadataVersion, setMetadataVersion] = useState(0);
  const [portalManagingAgentDefaults, setPortalManagingAgentDefaults] = useState({});
  const [propertyRowVersion, setPropertyRowVersion] = useState(1);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [propertyData, fme1Fields, fme1Checkboxes, portalDefaultsData] = await Promise.all([
          getPropertyDetails(propertyId),
          getFME1FormFieldSchema(),
          getFME1CheckboxSchema(),
          getPortalWideLpe1Section12Defaults(),
        ]);
        if (!active) return;
        setProperty(propertyData);
        setPropertyRowVersion(propertyData?.row_version || 1);
        const nextMetadata =
          propertyData?.metadata?.fme1 && typeof propertyData.metadata.fme1 === "object"
            ? propertyData.metadata.fme1
            : {};
        draftRef.current = nextMetadata;
        setMetadataVersion((prev) => prev + 1);
        setPortalManagingAgentDefaults(portalDefaultsData || {});
        setTextSchema(
          (fme1Fields || [])
            .filter((field) => field.section >= 1 && field.section <= 6)
            .sort((left, right) => left.order - right.order)
        );
        setCheckboxSchema(
          (fme1Checkboxes || [])
            .filter((field) => field.section >= 1 && field.section <= 6)
            .sort((left, right) => left.order - right.order)
        );
      } catch (error) {
        console.error("Failed to load FME1 metadata:", error);
        if (active) {
          setToast({
            open: true,
            message: "Failed to load FME1 defaults",
            severity: "error",
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [propertyId]);

  useEffect(() => {
    setCustomPath([
      { label: "Properties", path: "/properties" },
      { label: property?.name || "Property", path: `/properties/${propertyId}/details?tab=property-enquiries` },
      { label: "FME1 Metadata", path: null },
    ]);
    return () => clearCustomPath();
  }, [clearCustomPath, property?.name, propertyId, setCustomPath]);

  const getDraftValue = (key) => draftRef.current?.[key] ?? "";
  const setDraftValue = (key, value) => {
    draftRef.current = {
      ...draftRef.current,
      [key]: value,
    };
  };

  const textFieldKeys = useMemo(
    () => new Set(textSchema.map((field) => field.field_key)),
    [textSchema]
  );

  const resolveTextKey = (...keys) => keys.find((key) => textFieldKeys.has(key)) || keys[0];

  const propertySubtitle = useMemo(() => {
    if (!property) return "";
    return `${property.name || "Property"} • ${property.address_line1 || ""}${property.address_line2 ? `, ${property.address_line2}` : ""}`;
  }, [property]);

  const schemaBySection = useMemo(() => {
    const next = new Map();

    textSchema.forEach((field) => {
      const sectionFields = next.get(field.section) || [];
      sectionFields.push({ ...field, kind: "text", sortOrder: field.order });
      next.set(field.section, sectionFields);
    });

    checkboxSchema.forEach((field) => {
      const sectionFields = next.get(field.section) || [];
      sectionFields.push({ ...field, kind: "checkbox", sortOrder: field.order + 1000 });
      next.set(field.section, sectionFields);
    });

    return [1, 2, 3, 4, 5, 6].map((section) => ({
      section,
      title: FME1_SECTION_TITLES[section],
      fields: (next.get(section) || []).sort((left, right) => left.sortOrder - right.sortOrder),
    }));
  }, [checkboxSchema, textSchema]);

  const Check = ({ label, checked, onChange }) => (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={onChange} />}
      label={label}
    />
  );

  const BufferedText = ({
    label,
    value: currentValue,
    onCommit,
    resetKey,
    disabled = false,
    readOnly = false,
    visuallyLocked = false,
    size,
    sx,
  }) => {
    const [local, setLocal] = useState(currentValue ?? "");

    useEffect(() => {
      setLocal(currentValue ?? "");
    }, [currentValue, resetKey]);

    return (
      <TextField
        label={label}
        fullWidth
        value={local}
        onChange={(event) => setLocal(event.target.value)}
        onBlur={() => !disabled && !readOnly && onCommit(local)}
        disabled={disabled}
        InputProps={{ readOnly }}
        size={size}
        sx={{
          ...(visuallyLocked
            ? {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "action.hover",
                  cursor: "not-allowed",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                },
                "& .MuiInputBase-input": {
                  cursor: "not-allowed",
                },
                "& .MuiInputLabel-root": {
                  color: "text.secondary",
                },
              }
            : {}),
          ...(sx || {}),
        }}
      />
    );
  };

  const getFieldLabel = (field) => {
    if (!field) return "";
    return field.question || field.field_key;
  };

  const cleanFieldStem = (fieldKey = "") =>
    fieldKey
      .replace(/^\d+\s+/, "")
      .replace(/^\d+\.\d+\s+/, "")
      .trim();

  const getConfiguredFieldLabel = (field, group) => {
    if (!field) return "";
    if (group?.fieldLabelOverrides?.[field.field_key]) {
      return group.fieldLabelOverrides[field.field_key];
    }
    if (group?.roleName && group?.labelPrefix) {
      const stem = cleanFieldStem(field.field_key || getFieldLabel(field));
      const displayStem = stem === "SC" ? "Sort Code" : stem;
      return `${group.labelPrefix} ${group.roleName} ${displayStem}`;
    }
    return getFieldLabel(field);
  };

  const getCheckboxQuestion = (field) => field?.question || field?.key || "";

  const getCheckboxOptions = (field) => field?.options || [];

  const getGroupFields = (group, fields) => {
    const byKey = new Map(fields.map((field) => [field.field_key, field]));
    const selected = [];
    const seen = new Set();

    (group.fieldKeys || []).forEach((fieldKey) => {
      const field = byKey.get(fieldKey);
      if (field && !seen.has(field.field_key)) {
        selected.push(field);
        seen.add(field.field_key);
      }
    });

    (group.fieldOrders || []).forEach((range) => {
      fields
        .filter((field) => isInRange(field.order, range))
        .forEach((field) => {
          if (!seen.has(field.field_key)) {
            selected.push(field);
            seen.add(field.field_key);
          }
        });
    });

    return selected.sort((left, right) => left.order - right.order);
  };

  const getGroupCheckboxes = (group, checkboxes) => {
    if (!group.checkboxOrders?.length) return [];
    return checkboxes.filter((field) => isInRange(field.order, group.checkboxOrders));
  };

  const renderFieldInput = (field, group = null, options = {}) => {
    const usesPortalDefault = FME1_PORTAL_MANAGING_AGENT_KEYS.has(field.field_key);
    return (
      <BufferedText
        key={field.field_key}
        label={options.label || getConfiguredFieldLabel(field, group)}
        value={usesPortalDefault ? portalManagingAgentDefaults?.[field.field_key] ?? "" : getDraftValue(field.field_key)}
        onCommit={(nextValue) => setDraftValue(field.field_key, nextValue)}
        resetKey={metadataVersion}
        readOnly={usesPortalDefault}
        visuallyLocked={usesPortalDefault}
        size={options.size}
        sx={options.sx}
      />
    );
  };

  const CheckboxQuestion = ({ config, compositeGroup = null }) => {
    const [selected, setSelected] = useState(getDraftValue(config.key));

    if (compositeGroup) {
      const selectedKey = compositeGroup.keys.find((key) => isCheckedValue(getDraftValue(key))) || "";

      return (
        <Box key={compositeGroup.keys.join("-")} sx={{ mb: 2 }}>
          <Typography variant="subtitle1">{compositeGroup.question}</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
            {compositeGroup.options.map((option) => (
              <Check
                key={`${compositeGroup.question}-${option.key}`}
                label={option.label}
                checked={selectedKey === option.key}
                onChange={() => {
                  const willCheck = selectedKey !== option.key;
                  compositeGroup.keys.forEach((key) => setDraftValue(key, ""));
                  if (willCheck) {
                    setDraftValue(option.key, "Checked");
                  }
                  setMetadataVersion((prev) => prev + 1);
                }}
              />
            ))}
          </Stack>
        </Box>
      );
    }

    return (
      <Box key={config.key} sx={{ mb: 2 }}>
        <Typography variant="subtitle1">{config.question}</Typography>
        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
          {(config.options || []).map((option) => (
            <Check
              key={`${config.key}-${option}`}
              label={option}
              checked={selected === option}
              onChange={() => {
                const nextValue = selected === option ? "" : option;
                setSelected(nextValue);
                setDraftValue(config.key, nextValue);
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const toggleNoticeParty = (party, isSelected) => {
    const amountKey = resolveTextKey(...party.amountKeys);
    setDraftValue(party.selectedKey, isSelected ? "" : "Checked");
    if (isSelected) {
      setDraftValue(amountKey, "");
      setDraftValue(party.emailKey, "");
    }
    setMetadataVersion((prev) => prev + 1);
  };

  const renderCheckedCheckbox = ({ label, checked, disabled = false, onChange }) => (
    <FormControlLabel
      control={<Checkbox checked={checked} disabled={disabled} onChange={onChange} />}
      label={label}
    />
  );

  const renderNoticeLayout = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        1.5 Notice of Transfer &amp; Charge
      </Typography>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">
            Who accepts service of the Notice of Transfer &amp; Charge?
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {FME1_NOTICE_PARTIES.map((party) => {
              const isSelected = isCheckedValue(getDraftValue(party.selectedKey));
              const emailSelected = isCheckedValue(getDraftValue(party.emailKey));
              const amountKey = resolveTextKey(...party.amountKeys);
              return (
                <Stack
                  key={party.selectedKey}
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", md: "center" }}
                  useFlexGap
                >
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleNoticeParty(party, isSelected)}
                      />
                    )}
                    label={party.label}
                    sx={{ minWidth: { md: 220 } }}
                  />
                  <BufferedText
                    label="Amount including VAT"
                    value={getDraftValue(amountKey)}
                    onCommit={(nextValue) => setDraftValue(amountKey, nextValue)}
                    resetKey={metadataVersion}
                    disabled={!isSelected}
                    size="small"
                  />
                  {renderCheckedCheckbox({
                    label: "Notice via email",
                    checked: emailSelected,
                    disabled: !isSelected,
                    onChange: () => {
                      setDraftValue(party.emailKey, emailSelected ? "" : "Checked");
                      setMetadataVersion((prev) => prev + 1);
                    },
                  })}
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Typography variant="subtitle1">If other, provide contact details for service:</Typography>

        <BufferedText
          label="Name"
          value={getDraftValue(resolveTextKey("1.6 Name", "6 Name"))}
          onCommit={(nextValue) => setDraftValue(resolveTextKey("1.6 Name", "6 Name"), nextValue)}
          resetKey={metadataVersion}
          size="small"
        />
        <BufferedText
          label="Address"
          value={getDraftValue(resolveTextKey("1.6 Address", "6 Address"))}
          onCommit={(nextValue) => setDraftValue(resolveTextKey("1.6 Address", "6 Address"), nextValue)}
          resetKey={metadataVersion}
          size="small"
        />
        <BufferedText
          label="Telephone"
          value={getDraftValue(resolveTextKey("1.6 Telephone", "6 Telephone"))}
          onCommit={(nextValue) => setDraftValue(resolveTextKey("1.6 Telephone", "6 Telephone"), nextValue)}
          resetKey={metadataVersion}
          size="small"
        />
        <BufferedText
          label="Email"
          value={getDraftValue(resolveTextKey("1.6 Email", "6 Email"))}
          onCommit={(nextValue) => setDraftValue(resolveTextKey("1.6 Email", "6 Email"), nextValue)}
          resetKey={metadataVersion}
          size="small"
        />
        <BufferedText
          label="Capacity"
          value={getDraftValue(resolveTextKey("1.6 Capacity eg Landlords lawyer", "6 Capacity eg Landlords lawyer"))}
          onCommit={(nextValue) => setDraftValue(resolveTextKey("1.6 Capacity eg Landlords lawyer", "6 Capacity eg Landlords lawyer"), nextValue)}
          resetKey={metadataVersion}
          size="small"
        />

        <Box>
          <Typography variant="subtitle1">
            1.5.1 Are details other than the incoming owner's name, address and lender name required?
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
            {["Yes", "No"].map((option) => (
              <Check
                key={`notice-details-${option}`}
                label={option}
                checked={getDraftValue("1.5.1 requires_additional_details") === option}
                onChange={() => {
                  setDraftValue(
                    "1.5.1 requires_additional_details",
                    getDraftValue("1.5.1 requires_additional_details") === option ? "" : option
                  );
                  setMetadataVersion((prev) => prev + 1);
                }}
              />
            ))}
          </Stack>
        </Box>

        <BufferedText
          label="1.5.1 If Yes, please detail required additional information"
          value={getDraftValue("1.5.1")}
          onCommit={(nextValue) => setDraftValue("1.5.1", nextValue)}
          resetKey={metadataVersion}
          size="small"
        />
      </Stack>
    </Box>
  );

  const renderPeriodFields = (group) => {
    if (!group.periodFields) return null;
    const placeholders = ["DD", "MM", "YYYY"];
    const renderDatePart = (fieldKey, index) => {
      const resolvedKey = resolveTextKey(fieldKey);
      return (
        <BufferedText
          key={resolvedKey}
          label={placeholders[index]}
          value={getDraftValue(resolvedKey)}
          onCommit={(nextValue) => setDraftValue(resolvedKey, nextValue)}
          resetKey={metadataVersion}
          size="small"
          sx={{ width: { xs: "100%", sm: index === 2 ? 120 : 90 } }}
        />
      );
    };

    return (
      <Box>
        <Typography variant="subtitle1">{group.periodFields.question}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }} alignItems={{ xs: "stretch", sm: "center" }} useFlexGap>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
            <Typography>From:</Typography>
            {group.periodFields.from.map(renderDatePart)}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" useFlexGap sx={{ flexWrap: "wrap" }}>
            <Typography>To:</Typography>
            {group.periodFields.to.map(renderDatePart)}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderDateRows = (group) => {
    if (!group.dateRows) return null;
    const placeholders = ["DD", "MM", "YYYY"];
    const renderDatePart = (fieldKey, index) => {
      const resolvedKey = resolveTextKey(fieldKey);
      return (
        <BufferedText
          key={resolvedKey}
          label={placeholders[index]}
          value={getDraftValue(resolvedKey)}
          onCommit={(nextValue) => setDraftValue(resolvedKey, nextValue)}
          resetKey={metadataVersion}
          size="small"
          sx={{ width: { xs: "100%", sm: index === 2 ? 120 : 90 } }}
        />
      );
    };

    return (
      <Box>
        <Typography variant="subtitle1">{group.dateRows.question}</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {group.dateRows.rows.map((row) => {
            const isSelected = isCheckedValue(getDraftValue(row.naKey));
            return (
              <Stack
                key={row.label}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                <Typography sx={{ width: { xs: "100%", sm: 90 } }}>{row.label}</Typography>
                {row.fields.map(renderDatePart)}
                {renderCheckedCheckbox({
                  label: "N/A",
                  checked: isSelected,
                  onChange: () => {
                    setDraftValue(row.naKey, isSelected ? "" : "Checked");
                    setMetadataVersion((prev) => prev + 1);
                  },
                })}
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  const renderFieldRows = (group) => {
    if (!group.fieldRows) return null;
    return (
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: group.question ? 1 : 0 }} useFlexGap>
        {group.fieldRows.map((row) => {
          const field = textSchema.find((candidate) => candidate.field_key === row.key);
          if (!field) return null;
          return renderFieldInput(field, group, { label: row.label, size: "small" });
        })}
      </Stack>
    );
  };

  const renderAnnualServiceChargeLayout = (group, groupFields) => {
    if (!group.annualServiceChargeLayout) return null;
    const amountField = groupFields.find((field) => field.field_key === "3.1 £");
    const detailsField = groupFields.find((field) => field.field_key === "3.1");

    return (
      <Box>
        <Typography variant="subtitle1">{amountField?.question || "3.1 What is the annual Service charge payable by this Property?"}</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {amountField ? renderFieldInput(amountField, group, { label: "Amount including VAT", size: "small" }) : null}
          {detailsField ? renderFieldInput(detailsField, group, { label: detailsField.question, size: "small" }) : null}
        </Stack>
      </Box>
    );
  };

  const renderReserveFundLayout = (group, groupFields) => {
    if (!group.reserveFundLayout) return null;
    const reserveFundQuestion = checkboxSchema.find((field) => field.key === "3.7");
    const reserveSufficientGroup = FME1_COMPOSITE_CHECKBOX_BY_KEY["3.7.2 reserve_fund_sufficient_yes"];
    const amountField = groupFields.find((field) => field.field_key === "3.7.1 £");
    const heldField = groupFields.find((field) => field.field_key === "3.7.1 Managed Areas £");
    const detailsField = groupFields.find((field) => field.field_key === "3.7.3");

    return (
      <Stack spacing={2}>
        {reserveFundQuestion ? (
          <CheckboxQuestion config={reserveFundQuestion} />
        ) : null}
        {amountField ? renderFieldInput(amountField, group, { size: "small" }) : null}
        {heldField ? renderFieldInput(heldField, group, { size: "small" }) : null}
        {reserveSufficientGroup ? (
          <CheckboxQuestion config={{ key: reserveSufficientGroup.keys[0] }} compositeGroup={reserveSufficientGroup} />
        ) : null}
        {detailsField ? renderFieldInput(detailsField, group, { size: "small" }) : null}
      </Stack>
    );
  };

  const renderCheckboxField = (field) => {
    const compositeGroup = FME1_COMPOSITE_CHECKBOX_BY_KEY[field.key];
    if (compositeGroup && compositeGroup.keys[0] !== field.key) return null;
    if (compositeGroup) {
      return <CheckboxQuestion key={`${field.key}-${metadataVersion}`} config={field} compositeGroup={compositeGroup} />;
    }

    const options = getCheckboxOptions(field);
    const currentValue = getDraftValue(field.key);
    if (options.length === 1 && options[0] === "Checked") {
      return renderCheckedCheckbox({
        label: getCheckboxQuestion(field),
        checked: isCheckedValue(currentValue),
        onChange: () => {
          setDraftValue(field.key, isCheckedValue(currentValue) ? "" : "Checked");
          setMetadataVersion((prev) => prev + 1);
        },
      });
    }

    return <CheckboxQuestion key={`${field.key}-${metadataVersion}`} config={{ ...field, question: getCheckboxQuestion(field), options }} />;
  };

  const renderSectionContent = (section) => {
    const fieldsForSection = textSchema.filter((field) => field.section === section.section);
    const checkboxesForSection = checkboxSchema.filter((field) => field.section === section.section);
    const groups = FME1_SECTION_GROUPS[section.section] || [];
    const renderedFieldKeys = new Set();
    const renderedCheckboxKeys = new Set();

    const markRendered = (fields, checkboxes) => {
      fields.forEach((field) => renderedFieldKeys.add(field.field_key));
      checkboxes.forEach((field) => renderedCheckboxKeys.add(field.key));
    };

    FME1_NOTICE_FIELD_KEYS.forEach((key) => {
      if (section.section === 1) renderedFieldKeys.add(key);
    });
    FME1_NOTICE_CHECKBOX_KEYS.forEach((key) => {
      if (section.section === 1) renderedCheckboxKeys.add(key);
    });

    return (
      <Stack spacing={4}>
        {groups.map((group, groupIndex) => {
          const groupFields = getGroupFields(group, fieldsForSection);
          const dateRowCheckboxKeys = new Set(group.dateRows?.rows.map((row) => row.naKey).filter(Boolean) || []);
          const groupCheckboxes = getGroupCheckboxes(group, checkboxesForSection).filter(
            (field) => !dateRowCheckboxKeys.has(field.key)
          );
          markRendered(groupFields, groupCheckboxes);
          dateRowCheckboxKeys.forEach((key) => renderedCheckboxKeys.add(key));

          const hasContent =
            group.noticeLayout ||
            group.periodFields ||
            group.dateRows ||
            group.fieldRows ||
            group.annualServiceChargeLayout ||
            group.reserveFundLayout ||
            groupFields.length ||
            groupCheckboxes.length;

          if (!hasContent) return null;

          return (
            <Box key={`section-${section.section}-group-${groupIndex}`}>
              {group.title ? <Typography variant="h6">{group.title}</Typography> : null}
              <Stack spacing={2} mt={group.title ? 1 : 0}>
                {group.noticeLayout ? renderNoticeLayout() : null}
                {renderPeriodFields(group)}
                {renderDateRows(group)}
                {group.question ? <Typography variant="subtitle1">{group.question}</Typography> : null}
                {renderFieldRows(group)}
                {renderAnnualServiceChargeLayout(group, groupFields)}
                {renderReserveFundLayout(group, groupFields)}
                {groupCheckboxes.length && !group.noticeLayout && !group.reserveFundLayout ? (
                  <Stack spacing={2}>{groupCheckboxes.map(renderCheckboxField)}</Stack>
                ) : null}
                {groupFields.length && !group.periodFields && !group.dateRows && !group.fieldRows && !group.annualServiceChargeLayout && !group.reserveFundLayout && !group.noticeLayout ? (
                  <Stack spacing={2}>{groupFields.map((field) => renderFieldInput(field, group))}</Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}

        {fieldsForSection.some((field) => !renderedFieldKeys.has(field.field_key)) || checkboxesForSection.some((field) => !renderedCheckboxKeys.has(field.key)) ? (
          <Box>
            <Stack spacing={2}>
              {fieldsForSection
                .filter((field) => !renderedFieldKeys.has(field.field_key) && !isNoticeField(field))
                .map((field) => renderFieldInput(field))}
              {checkboxesForSection
                .filter((field) => !renderedCheckboxKeys.has(field.key) && !isNoticeField(field))
                .map(renderCheckboxField)}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await updatePropertyMetadata(
        propertyId,
        { ...draftRef.current, row_version: propertyRowVersion },
        { scope: "fme1" }
      );
      if (response?.data?.row_version) {
        setPropertyRowVersion(response.data.row_version);
      }
      setToast({
        open: true,
        message: "Metadata updated",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save FME1 defaults:", error);
      setToast({
        open: true,
        message: getApiErrorMessage(error, "Failed to update metadata"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight={600}>
            FME1 Metadata
          </Typography>
          <Button variant="text" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>

        {loading ? (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={240} sx={{ mt: 2 }} />
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            {propertySubtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {propertySubtitle}
              </Typography>
            ) : null}
            {schemaBySection.map((section) => (
              <Accordion key={section.section} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Section {section.section}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>{renderSectionContent(section)}</AccordionDetails>
              </Accordion>
            ))}

            <Divider sx={{ my: 2 }} />

            <Button variant="contained" disabled={saving} onClick={handleSave}>
              Save Information
            </Button>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
